import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { usePageState } from "@/hooks/usePageState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  pageStateQueryOptions,
  PageAccessError,
  iamUsersInfiniteQueryOptions,
  iamTenantsInfiniteQueryOptions,
  invitesInfiniteQueryOptions,
  tenantPickerQueryOptions,
} from "@/lib/queries";
import {
  defaultIamTenantsListFilters,
  defaultIamUsersListFiltersForSession,
  defaultInvitesListFilters,
} from "@/features/identity-hub/lib/iam-list-filters";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { IdentityHubDashboard } from "@/features/identity-hub/components/dashboard/IdentityHubDashboard";
import { PageError } from "@/features/auth/components/PageError";

export const Route = createFileRoute("/_authenticated/iam")({
  beforeLoad: () => assertPagePermission("identity-hub"),
  loader: async ({ context }) => {
    // Block on the access check — without page state we can't decide whether
    // to render the dashboard or a 403, so it must resolve before the route
    // commits.
    const pageState = await context.queryClient
      .ensureQueryData(pageStateQueryOptions("identity-hub"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });

    // Warm the cache for the dashboard's primary queries in parallel.
    // Use the same default filter factories as IdentityHubDashboard so query
    // keys match and prefetch actually hits cache on mount.
    const { activeTenantId, tenants } = useAuth.getState();
    const isSuperTenant = tenants.some((t) => t.type === "SUPER_TENANT");
    void context.queryClient.prefetchInfiniteQuery(
      iamUsersInfiniteQueryOptions(
        defaultIamUsersListFiltersForSession(activeTenantId, isSuperTenant),
      ),
    );
    void context.queryClient.prefetchInfiniteQuery(
      iamTenantsInfiniteQueryOptions(defaultIamTenantsListFilters(isSuperTenant)),
    );
    void context.queryClient.prefetchInfiniteQuery(
      invitesInfiniteQueryOptions(defaultInvitesListFilters()),
    );
    void context.queryClient.prefetchQuery(tenantPickerQueryOptions());

    return pageState;
  },
  component: IamPage,
  errorComponent: ({ error }) => (
    <PageError is403={error instanceof PageAccessError} />
  ),
});

function IamPage() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.identityHub"));
  const { isLoading, error } = usePageState("identity-hub");

  if (isLoading) {
    return <div className="flex h-full items-center justify-center">Loading…</div>;
  }

  if (error) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  return <IdentityHubDashboard />;
}
